import apiClient from '../../../services/apiClient';

// Helper function to check if token is expired or about to expire
const isTokenExpiredOrExpiringSoon = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    // Check if token expires in next 10 minutes (600 seconds)
    return payload.exp - currentTime < 600;
  } catch (error) {
    return true; // If we can't decode token, consider it expired
  }
};

// Note: Auth-specific interceptors are handled in the main apiClient.js

export const authService = {
  // Login user (Step 1: Send verification code)
  async login(email, password) {
    try {
      const response = await apiClient.post('/auth/login', {
        email,
        password,
      });
      
      // Login endpoint only sends verification code, does not return tokens
      // Tokens are returned after verification in verifyCode()
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Login failed');
    }
  },

  // Register user
  async register(userData) {
    try {
      const response = await apiClient.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Registration failed');
    }
  },

  // Forgot password
  async forgotPassword(email) {
    try {
      const response = await apiClient.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Password reset request failed');
    }
  },

  // Reset password
  async resetPassword(token, newPassword) {
    try {
      const response = await apiClient.post('/auth/reset-password', {
        token,
        new_password: newPassword,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Password reset failed');
    }
  },

  // Verify code
  async verifyCode(email, code) {
    try {
      const response = await apiClient.post('/auth/verify', {
        email,
        code,
      });
      
      const { access_token, refresh_token, user } = response.data;
      
      // Store token and user data
      localStorage.setItem('authToken', access_token);
      if (refresh_token) {
        localStorage.setItem('refreshToken', refresh_token);
      }
      localStorage.setItem('user', JSON.stringify(user));
      
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Verification failed');
    }
  },

  // Resend verification code
  async resendVerificationCode(email) {
    try {
      const response = await apiClient.post('/auth/resend-verification', { email });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to resend verification code');
    }
  },

  // Logout user (complete logout)
  logout() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedRole');
    window.location.href = '/auth/login';
  },

  // Logout to role selection (keep user logged in but clear role)
  logoutToRoleSelection() {
    localStorage.removeItem('selectedRole');
    window.location.href = '/auth/role-selection';
  },

  // Get current user
  getCurrentUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  // Check if user is authenticated
  isAuthenticated() {
    return !!localStorage.getItem('authToken');
  },

  // Refresh token
  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      const response = await apiClient.post('/auth/refresh', {
        refresh_token: refreshToken
      });
      
      const { access_token, refresh_token: newRefreshToken } = response.data;
      localStorage.setItem('authToken', access_token);
      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
      }
      
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Token refresh failed');
    }
  },

  // Check token expiry and refresh if needed
  async checkAndRefreshToken() {
    const token = localStorage.getItem('authToken');
    if (!token) return false;
    
    if (isTokenExpiredOrExpiringSoon(token)) {
      try {
        await this.refreshToken();
        return true;
      } catch (error) {
        // Refresh failed, logout user
        this.logout();
        return false;
      }
    }
    return true;
  },

  // Start automatic token refresh checking
  startTokenRefreshInterval() {
    // Check every 5 minutes
    return setInterval(() => {
      this.checkAndRefreshToken();
    }, 5 * 60 * 1000);
  },

  // Update user profile
  async updateProfile(userData) {
    try {
      const response = await apiClient.put('/auth/profile', userData);
      const { user } = response.data;
      localStorage.setItem('user', JSON.stringify(user));
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Profile update failed');
    }
  },

  // Change password
  async changePassword(currentPassword, newPassword) {
    try {
      const response = await apiClient.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Password change failed');
    }
  },
};

export default authService; 